/**
 * Typed API client.
 *
 * Auth rides on the httpOnly session cookie, so `credentials: 'include'` is set
 * everywhere and no token is ever held in JavaScript — an XSS bug in the UI
 * cannot exfiltrate a session.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

const BASE = '/api/v1';

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  // Collapse concurrent refreshes: a grid view fires many requests at once and
  // each 401 would otherwise rotate the refresh token, invalidating the others.
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * How long to wait before retrying a rate-limited request.
 *
 * Honours Retry-After when the server sends one, and otherwise backs off
 * exponentially with jitter. The jitter matters here: two dozen uploads run in
 * parallel, so a fixed delay would send every one of them back at the same
 * instant and trip the limit again.
 */
function rateLimitDelay(attempt: number, retryAfter: string | null): number {
  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds > 0) return Math.min(seconds * 1000, 30_000);

  const base = Math.min(500 * 2 ** attempt, 8_000);
  return base + Math.random() * base;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Attempts a rate-limited request gets before it is reported as failed. */
const RATE_LIMIT_ATTEMPTS = 5;

async function request<T>(
  path: string,
  init: RequestInit = {},
  retryOn401 = true,
  attempt = 0,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { 'content-type': 'application/json' }
        : {}),
      ...init.headers,
    },
  });

  if (res.status === 401 && retryOn401) {
    if (await refreshSession()) return request<T>(path, init, false, attempt);
  }

  // Being told to slow down is an instruction, not a failure. Surfacing it as
  // one during a bulk upload marks perfectly good files as broken and leaves
  // the user to retry by hand what the client could have waited out.
  if (res.status === 429 && attempt < RATE_LIMIT_ATTEMPTS) {
    await wait(rateLimitDelay(attempt, res.headers.get('retry-after')));
    return request<T>(path, init, retryOn401, attempt + 1);
  }

  if (!res.ok) {
    let code = 'request_failed';
    let message = `Request failed (${res.status})`;
    let details: unknown;
    try {
      const body = (await res.json()) as { error?: string; message?: string; details?: unknown };
      code = body.error ?? code;
      message = body.message ?? message;
      details = body.details;

      // Validation failures carry per-field detail. Without it the user sees
      // "Request did not match the expected shape", which says nothing about
      // which field, or why.
      if (code === 'validation_failed' && Array.isArray(details)) {
        const fields = (details as Array<{ path?: string; message?: string }>)
          .map((d) => (d.path ? `${d.path}: ${d.message}` : d.message))
          .filter(Boolean);
        if (fields.length > 0) message = fields.join('; ');
      }
    } catch {
      // Non-JSON error body (proxy timeout, HTML error page).
    }
    throw new ApiRequestError(res.status, code, message, details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- types ------------------------------------------------------------------

export interface Me {
  id: string;
  discordId: string;
  username: string;
  displayName: string;
  avatarUrl: string;
  tier: number;
  tierName: string;
  roles: string[];
  quotaBytes: number | null;
}

export interface Asset {
  id: string;
  folder_id: string | null;
  filename: string;
  ext: string | null;
  mime: string;
  kind: 'image' | 'video' | 'audio' | 'other';
  size_bytes: number;
  content_hash: string;
  status: string;
  width: number | null;
  height: number | null;
  duration_s: number | null;
  fps: number | null;
  video_codec: string | null;
  captured_at: string | null;
  camera_make: string | null;
  camera_model: string | null;
  lens: string | null;
  iso: number | null;
  f_number: number | null;
  focal_length: number | null;
  gps_lat: number | null;
  gps_lon: number | null;
  has_thumb: boolean;
  has_proxy: boolean;
  /** A graded rendition exists for this still. */
  has_edit: boolean;
  vision_status: string;
  transcript_status: string;
  created_at: string;
}

export interface Folder {
  id: string;
  parent_id: string | null;
  name: string;
  path: string;
  asset_count?: number;
  total_bytes?: number;
  child_count?: number;
}

export interface Facets {
  kinds: Array<{ value: string; count: number }>;
  cameras: Array<{ camera_make: string | null; camera_model: string; count: number }>;
  lenses: Array<{ value: string; count: number }>;
  tags: Array<{ value: string; kind: string; count: number }>;
  years: Array<{ value: number; count: number }>;
  extensions: Array<{ value: string; count: number }>;
  people: Array<{ value: string; label: string | null; count: number; cover_asset_id: string | null }>;
}

export interface SearchResult {
  assets: Asset[];
  nextCursor: string | null;
  hasMore: boolean;
  searchMode: 'semantic' | 'text' | 'browse';
}

export type SearchParams = Record<string, string | number | boolean | string[] | undefined | null>;

function toQueryString(params: SearchParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

// --- endpoints --------------------------------------------------------------

export const api = {
  me: () => request<Me>('/auth/me'),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  listAssets: (params: SearchParams) =>
    request<SearchResult>(`/assets${toQueryString(params)}`),

  getAsset: (id: string) =>
    request<{
      asset: Asset & { folder_path: string | null; uploader_username: string | null };
      tags: Array<{ id: string; name: string; kind: string; confidence: number | null; source: string }>;
      faces: Array<{ id: string; bbox: Record<string, number>; cluster_id: string | null; label: string | null }>;
      transcript: { language: string | null; text: string; segments: Array<{ start: number; end: number; text: string }> } | null;
      metadata: {
        ffprobe: unknown;
        exif: Record<string, unknown> | null;
        edit?: Record<string, number> | null;
      } | null;
    }>(`/assets/${id}`),

  facets: (folderId?: string | null) =>
    request<Facets>(`/assets/facets${toQueryString({ folderId })}`),

  updateAsset: (id: string, body: { filename?: string; folderId?: string | null }) =>
    request<{ asset: Asset }>(`/assets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteAsset: (id: string) => request<{ ok: boolean }>(`/assets/${id}`, { method: 'DELETE' }),

  reindex: (body: {
    assetIds?: string[];
    folderId?: string;
    /** Every still or video with no thumbnail, archive-wide. */
    missingThumbnails?: boolean;
    recursive?: boolean;
    stages: Array<'ingest' | 'vision' | 'transcript'>;
    force?: boolean;
  }) =>
    request<{
      queued: number;
      byStage: Record<string, number>;
      candidates?: number;
      truncated?: boolean;
    }>('/assets/reindex', { method: 'POST', body: JSON.stringify(body) }),

  bulkDeleteAssets: (assetIds: string[]) =>
    request<{
      deleted: number;
      denied: number;
      notFound: number;
      deniedExamples: string[];
      recoverable: boolean;
    }>('/assets/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ assetIds }),
    }),

  /**
   * Asks the server to assemble a zip of the selection.
   *
   * Returns a URL rather than the archive itself: the bytes have to arrive as a
   * browser download, and a download cannot report that half the selection was
   * in a folder you cannot read. Everything that can fail fails here, where it
   * can be shown on the page.
   */
  prepareDownload: (assetIds: string[]) =>
    request<{
      token: string;
      url: string;
      filename: string;
      files: number;
      bytes: number;
      denied: number;
      missing: number;
      notFound: number;
      expiresInSeconds: number;
    }>('/assets/download', {
      method: 'POST',
      body: JSON.stringify({ assetIds }),
    }),

  /** Moves a selection into one folder, or to the top level with null. */
  bulkMoveAssets: (assetIds: string[], folderId: string | null) =>
    request<{
      moved: number;
      denied: number;
      unchanged: number;
      notFound: number;
      deniedExamples: string[];
    }>('/assets/bulk-move', {
      method: 'POST',
      body: JSON.stringify({ assetIds, folderId }),
    }),

  addTags: (id: string, tags: string[]) =>
    request<{ ok: boolean }>(`/assets/${id}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tags }),
    }),

  removeTag: (id: string, tagId: string) =>
    request<{ ok: boolean }>(`/assets/${id}/tags/${tagId}`, { method: 'DELETE' }),

  listFolders: (params: { parentId?: string | null; tree?: boolean } = {}) =>
    request<{ folders: Folder[] }>(`/folders${toQueryString(params)}`),

  getFolder: (id: string) =>
    request<{
      folder: Folder;
      ancestors: Folder[];
      permissions: { tier: number; tierName: string };
    }>(`/folders/${id}`),

  /** Resolves a path to a folder, creating missing levels. Idempotent. */
  ensureFolder: (segments: string[], parentId: string | null) =>
    request<{ folderId: string; path: string; created: string[] }>('/folders/ensure', {
      method: 'POST',
      body: JSON.stringify({ segments, parentId }),
    }),

  createFolder: (body: { name: string; parentId: string | null }) =>
    request<{ folder: Folder }>('/folders', { method: 'POST', body: JSON.stringify(body) }),

  renameFolder: (id: string, body: { name?: string; parentId?: string | null }) =>
    request<{ folder: Folder }>(`/folders/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteFolder: (id: string, force = false) =>
    request<{ ok: boolean; deletedAssets: number }>(
      `/folders/${id}${toQueryString({ force })}`,
      { method: 'DELETE' },
    ),

  // --- uploads ---
  createUpload: (body: { filename: string; size: number; folderId: string | null; contentHash?: string }) =>
    request<
      | {
          deduplicated: true;
          assetId: string;
          existingFilename: string;
          message: string;
          /** True when a row was created in the target folder for this upload. */
          linked?: boolean;
        }
      | { deduplicated: false; uploadId: string; offset: number; chunkSize: number }
    >('/uploads', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Sends one chunk, reporting real upload progress and renewing an expired
   * session first.
   *
   * XMLHttpRequest rather than fetch, for one reason: fetch cannot report how
   * much of a request body has been sent. With 64 MiB chunks that meant the
   * progress bar moved once per chunk and sat still in between, so a transfer
   * that was working perfectly looked like it had hung -- and the further
   * along a file was, the longer the stall appeared.
   *
   * This also cannot go through request(): the body is a Blob and the error
   * carries a resume offset. It still needs the same 401 handling, or an
   * upload outliving its access token dies partway through with a valid
   * refresh token sitting unused.
   */
  uploadChunk: (
    uploadId: string,
    offset: number,
    chunk: Blob,
    opts: {
      signal?: AbortSignal;
      onProgress?: (bytesSentInChunk: number) => void;
      retryOn401?: boolean;
      attempt?: number;
    } = {},
  ): Promise<{ offset: number; complete: boolean }> => {
    const { signal, onProgress, retryOn401 = true, attempt = 0 } = opts;

    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Upload cancelled', 'AbortError'));
        return;
      }

      const xhr = new XMLHttpRequest();
      xhr.open('PATCH', `${BASE}/uploads/${uploadId}`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('content-type', 'application/offset+octet-stream');
      xhr.setRequestHeader('upload-offset', String(offset));

      const onAbort = (): void => xhr.abort();
      signal?.addEventListener('abort', onAbort);

      const cleanup = (): void => signal?.removeEventListener('abort', onAbort);

      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) onProgress(event.loaded);
        };
      }

      xhr.onerror = () => {
        cleanup();
        reject(new ApiRequestError(0, 'network_error', 'Network error during upload'));
      };

      xhr.onabort = () => {
        cleanup();
        reject(new DOMException('Upload cancelled', 'AbortError'));
      };

      xhr.onload = () => {
        cleanup();

        if (xhr.status === 429 && attempt < RATE_LIMIT_ATTEMPTS) {
          const delay = rateLimitDelay(attempt, xhr.getResponseHeader('retry-after'));
          void wait(delay).then(() => {
            api
              .uploadChunk(uploadId, offset, chunk, { ...opts, attempt: attempt + 1 })
              .then(resolve, reject);
          });
          return;
        }

        if (xhr.status === 401 && retryOn401) {
          void refreshSession().then((ok) => {
            if (!ok) {
              reject(new ApiRequestError(401, 'chunk_failed', 'Session expired'));
              return;
            }
            api
              .uploadChunk(uploadId, offset, chunk, { ...opts, retryOn401: false })
              .then(resolve, reject);
          });
          return;
        }

        let body: { message?: string; details?: { expectedOffset?: number } } = {};
        try {
          body = JSON.parse(xhr.responseText) as typeof body;
        } catch {
          // Non-JSON body: a proxy error page, most likely.
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          reject(
            new ApiRequestError(
              xhr.status,
              'chunk_failed',
              body.message ?? `Chunk upload failed (${xhr.status})`,
              body.details,
            ),
          );
          return;
        }

        resolve(body as unknown as { offset: number; complete: boolean });
      };

      xhr.send(chunk);
    });
  },

  completeUpload: (uploadId: string) =>
    request<{ assetId: string; contentHash: string; deduplicated: boolean }>(
      `/uploads/${uploadId}/complete`,
      { method: 'POST' },
    ),

  abortUpload: (uploadId: string) =>
    request<{ ok: boolean }>(`/uploads/${uploadId}`, { method: 'DELETE' }),

  // --- edits ---
  createEdit: (body: Record<string, unknown>) =>
    request<{ jobId: string; status: string }>('/edits', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listEdits: () =>
    request<{
      jobs: Array<{
        id: string;
        kind: string;
        status: string;
        progress: number;
        output_asset_id: string | null;
        log: string | null;
        created_at: string;
      }>;
    }>('/edits'),

  // --- device pairing ---
  approveDevice: (userCode: string) =>
    request<{ ok: boolean; deviceName: string }>('/auth/device/approve', {
      method: 'POST',
      body: JSON.stringify({ userCode }),
    }),

  listDevices: () =>
    request<{
      devices: Array<{
        id: string;
        name: string;
        platform: string;
        sync_root_path: string | null;
        sync_cursor: number;
        last_seen_at: string | null;
      }>;
    }>('/auth/devices'),

  removeDevice: (id: string) =>
    request<{ ok: boolean }>(`/auth/devices/${id}`, { method: 'DELETE' }),

  // --- pins (force-sync folders) ---
  getPins: () => request<{ folderIds: string[] }>('/sync/pins'),
  setPins: (folderIds: string[]) =>
    request<{ ok: boolean; folderIds: string[] }>('/sync/pins', {
      method: 'PUT',
      body: JSON.stringify({ folderIds }),
    }),

  // --- people (face clusters) ---
  listFaceClusters: (named: 'all' | 'named' | 'unnamed' = 'all', minFaces = 2) =>
    request<{
      clusters: Array<{
        id: string;
        label: string | null;
        face_count: number;
        asset_count: number;
        cover_asset_id: string | null;
      }>;
    }>(`/faces/clusters${toQueryString({ named, minFaces })}`),

  getFaceCluster: (id: string) =>
    request<{
      cluster: { id: string; label: string | null; face_count: number; cover_asset_id: string | null };
      faces: Array<{
        id: string;
        asset_id: string;
        bbox: Record<string, number>;
        filename: string;
        has_thumb: boolean;
      }>;
    }>(`/faces/clusters/${id}`),

  nameFaceCluster: (id: string, label: string | null) =>
    request<{ cluster: unknown }>(`/faces/clusters/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ label }),
    }),

  mergeFaceClusters: (targetId: string, sourceClusterIds: string[]) =>
    request<{ facesMoved: number }>(`/faces/clusters/${targetId}/merge`, {
      method: 'POST',
      body: JSON.stringify({ sourceClusterIds }),
    }),

  previewClustering: (threshold?: number) =>
    request<{
      faces: number;
      wouldBeClusters: number;
      currentClusters: number;
      threshold: number;
    }>(`/faces/preview${toQueryString({ threshold })}`),

  rebuildFaceClusters: () =>
    request<{
      facesProcessed: number;
      clustersBefore: number;
      clustersAfter: number;
      namesPreserved: number;
      namesLost: string[];
      threshold: number;
    }>('/faces/rebuild', { method: 'POST' }),

  detachFace: (faceId: string) =>
    request<{ ok: boolean; newClusterId: string }>(`/faces/faces/${faceId}/detach`, {
      method: 'POST',
    }),

  // --- admin ---
  adminUsers: (search?: string) =>
    request<{
      users: Array<{
        id: string;
        username: string;
        global_name: string | null;
        discord_id: string;
        tier: number;
        tier_override: number | null;
        effectiveTier: number;
        tierName: string;
        is_active: boolean;
        upload_count: number;
        bytes_uploaded: number;
        last_login_at: string | null;
      }>;
    }>(`/admin/users${toQueryString({ search })}`),

  adminUpdateUser: (id: string, body: { tierOverride?: number | null; isActive?: boolean }) =>
    request<{ user: unknown }>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  adminRoleMap: () =>
    request<{
      mappings: Array<{
        discord_role_id: string;
        label: string;
        tier: number;
        tierName: string;
        member_count: number;
      }>;
      availableTiers: Array<{ name: string; value: number }>;
    }>('/admin/role-map'),

  adminSetRole: (body: { discordRoleId: string; label: string; tier: number }) =>
    request<{ ok: boolean }>('/admin/role-map', { method: 'PUT', body: JSON.stringify(body) }),

  adminDeleteRole: (roleId: string) =>
    request<{ ok: boolean }>(`/admin/role-map/${roleId}`, { method: 'DELETE' }),

  adminTrash: (search?: string) =>
    request<{
      items: Array<{
        id: string;
        filename: string;
        kind: string;
        size_bytes: number;
        deleted_at: string;
        folder_path: string | null;
        uploader: string | null;
        has_thumb: boolean;
        shares_bytes_with_live: boolean;
      }>;
      summary: {
        count: number;
        bytes: number;
        reclaimableBytes: number;
        oldestDeletedAt: string | null;
      };
    }>(`/admin/trash${toQueryString({ search })}`),

  adminRestore: (assetIds: string[]) =>
    request<{ restored: number; requeued: number; processing: number }>('/admin/trash/restore', {
      method: 'POST',
      body: JSON.stringify({ assetIds }),
    }),

  adminPurge: (body: { assetIds?: string[]; olderThanDays?: number; dryRun?: boolean }) =>
    request<{
      assetsPurged: number;
      blobsDeleted: number;
      blobsRetained: number;
      bytesReclaimed: number;
      derivativesDeleted: number;
      errors: string[];
    }>('/admin/trash/purge', { method: 'POST', body: JSON.stringify(body) }),

  saveEdit: (assetId: string, edit: Readonly<Record<string, number>> | object) =>
    request<{ edit: Record<string, number>; rendering: boolean }>(`/assets/${assetId}/edit`, {
      method: 'PUT',
      body: JSON.stringify(edit),
    }),

  clearEdit: (assetId: string) =>
    request<{ ok: boolean }>(`/assets/${assetId}/edit`, { method: 'DELETE' }),

  sidecarSurvey: () =>
    request<{
      candidates: Array<{
        sidecarAssetId: string;
        sidecarFilename: string;
        folderPath: string | null;
        status: string;
        targetAssetId?: string;
        targetFilename?: string;
        confidence?: 'corroborated' | 'filename-only';
        matchedOn?: string[];
        reason?: string;
        keywords?: string[];
        rating?: number;
      }>;
      byStatus: Record<string, number>;
    }>('/admin/sidecars'),

  sidecarMerge: (body: { requireCorroboration: boolean; sidecarAssetIds?: string[] }) =>
    request<{ merged: number; tagsAdded: number; skipped: number; errors: string[] }>(
      '/admin/sidecars/merge',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  sidecarUnmerge: (assetId: string) =>
    request<{ ok: boolean }>('/admin/sidecars/unmerge', {
      method: 'POST',
      body: JSON.stringify({ assetId }),
    }),

  maintenanceSurvey: () =>
    request<{
      categories: Array<{
        category: string;
        label: string;
        count: number;
        description: string;
      }>;
    }>('/admin/maintenance'),

  maintenanceRun: (categories: string[]) =>
    request<{
      removed: Record<string, number>;
      bytesReclaimed: number;
      errors: string[];
    }>('/admin/maintenance', {
      method: 'POST',
      body: JSON.stringify({ categories }),
    }),

  adminStats: () =>
    request<{
      totals: { assets: number; bytes: number; folders: number; users: number };
      byKind: Array<{ kind: string; count: number; bytes: number }>;
      visionQueue: Array<{ status: string; count: number }>;
      webBuild: {
        checked: boolean;
        stale: boolean;
        newestSource?: string;
        staleByMinutes?: number;
      };
    }>('/admin/stats'),

  adminProcessing: () =>
    request<{
      queues: Record<string, Record<string, number>> | null;
      active: Array<{ id: string; queue: string; assetId: string | null; startedAt: number | null; attempts: number }>;
      failures: Array<{ id: string; queue: string; assetId: string | null; failedReason?: string; enqueuedAt: number; attempts: number }>;
      pipeline: Record<string, number> | null;
      stuck: Array<{ id: string; filename: string; status: string; vision_status: string; created_at: string }>;
      throughput: Record<string, number> | null;
    }>('/admin/processing'),

  adminRetry: (kind: 'ingest' | 'vision' | 'transcript', assetIds?: string[]) =>
    request<{ retried: number }>('/admin/processing/retry', {
      method: 'POST',
      body: JSON.stringify({ kind, ...(assetIds ? { assetIds } : {}) }),
    }),

  adminStorage: () =>
    request<{
      nas: { path: string; totalBytes: number; freeBytes: number; usedBytes: number; percentUsed: number; available: boolean };
      cache: { path: string; totalBytes: number; freeBytes: number; usedBytes: number; percentUsed: number; available: boolean };
      archive: { files: number; bytes: number } | null;
      byKind: Array<{ kind: string; files: number; bytes: number }>;
      trash: { files: number; bytes: number; reclaimable: number } | null;
      growth: Array<{ day: string; files: number; bytes: number }>;
      largestFolders: Array<{ id: string; path: string; files: number; bytes: number }>;
      duplication: { total_rows: number; unique_files: number; saved: number } | null;
      projection: { bytesPerDay: number; daysUntilFull: number | null };
    }>('/admin/storage'),

  health: () =>
    request<{
      ok: boolean;
      storage: { ok: boolean; mediaRoot: string };
      visionService: boolean | null;
      queues: Record<string, Record<string, number>> | null;
      websocketClients: number;
    }>('/health'),
};

// --- helpers ----------------------------------------------------------------

export const assetUrls = {
  thumb: (id: string) => `${BASE}/assets/${id}/thumb`,
  proxy: (id: string) => `${BASE}/assets/${id}/proxy`,
  content: (id: string) => `${BASE}/assets/${id}/content`,
  /** The graded rendition, when one has been produced. */
  edited: (id: string) => `${BASE}/assets/${id}/edited`,
  /** The Camera Raw settings, for Lightroom or darktable. */
  editXmp: (id: string) => `${BASE}/assets/${id}/edit.xmp`,
  download: (id: string) => `${BASE}/assets/${id}/content?download=true`,
};

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(value >= 100 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function loginUrl(next?: string): string {
  const params = next ? `?next=${encodeURIComponent(next)}` : '';
  return `${BASE}/auth/discord/login${params}`;
}
